import { reduce } from 'lodash/collection';
import { isArray } from 'lodash/lang';

import GeoApi from '../../api/GeoApi';
import { appInfo } from '../../root';

import iso3166Countries from './countries.json';

const countries = iso3166Countries.filter(function({ id }) {
  if (!appInfo.features.globalAddressesEnabled) {
    return ['US', 'CA'].includes(id);
  }
  return true;
});

const countryIdMap = generateIdValueMap(countries);

class Geo {
  constructor() {
    this.countryToRegionsMap = {};
  }

  async getRegion(countryId, regionId) {
    if (this.#shouldLoadRegions(countryId)) {
      await this.#loadRegions(countryId);
    }
    return this.countryToRegionsMap[countryId]?.regionIdMap?.[regionId] || null;
  }

  async getRegions(countryId) {
    if (this.#shouldLoadRegions(countryId)) {
      await this.#loadRegions(countryId);
    }

    return this.countryToRegionsMap[countryId]?.regions || [];
  }

  #shouldLoadRegions(countryId) {
    return !this.countryToRegionsMap[countryId] && !!countryId;
  }

  async #loadRegions(countryId) {
    return GeoApi.fetchRegions(countryId).then(
      ({ data }) => this.#setRegions(countryId, data),
      () => this.#setRegions(countryId, null)
    );
  }

  #setRegions(countryId, regions) {
    this.countryToRegionsMap[countryId] = {
      regions,
      regionIdMap: generateIdValueMap(regions)
    };
  }
}

function getCountryById(id) {
  return countryIdMap[id] || null;
}

function getCountryName(id) {
  const country = getCountryById(id);
  return country?.localName || country?.name || null;
}

function getDialCode(id) {
  return getCountryById(id)?.dialCode || null;
}

function calcDialCodeLength(id) {
  return getDialCode(id)?.length || 0;
}

function generateIdValueMap(list) {
  if (!isArray(list)) {
    return {};
  }
  return reduce(
    list,
    function(accumulator, value) {
      accumulator[value.id] = value;
      return accumulator;
    },
    {}
  );
}

function getPostalCodeRegex(countryId) {
  if (countryId === 'US') {
    return /(^\d{5}$)|(^\d{5}-\d{4}$)/;
  }
  if (countryId === 'CA') {
    return /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z] [0-9][ABCEGHJ-NPRSTV-Z][0-9]$/;
  }
  return /^[A-Za-z0-9\- ]{3,12}$/;
}

export {
  calcDialCodeLength,
  countries,
  generateIdValueMap,
  getCountryById,
  getCountryName,
  getDialCode,
  getPostalCodeRegex
};

export default new Geo();
